import { useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";
import { readerScrollBehavior } from "@/lib/scrollBehavior";
import { focusTarget } from "@/lib/motion";

const ContentsLinks = ({ sections, activeId, prefix, onNavigate }) => sections.map((section, index) => (
  <a
    key={section.id}
    className={activeId === section.id ? "is-active" : ""}
    href={`#${section.id}`}
    aria-current={activeId === section.id ? "location" : undefined}
    onClick={onNavigate}
    data-testid={`${prefix}-toc-link-${section.id}`}
  >
    <span aria-hidden="true">{section.number ?? String(index + 1).padStart(2, "0")}</span>{section.title}
  </a>
));

export const TableOfContents = ({ sections }) => {
  const [activeId, setActiveId] = useState(sections[0]?.id ?? "");
  const [mobileOpen, setMobileOpen] = useState(false);
  const handleNavigate = (event) => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    setMobileOpen(false);
    const hash = event.currentTarget.hash;
    if (window.location.hash === hash) {
      event.preventDefault();
      const target = document.getElementById(hash.slice(1));
      focusTarget(target);
      target?.scrollIntoView({ behavior: readerScrollBehavior() });
    }
  };

  useEffect(() => {
    const observedSections = sections
      .map(({ id }) => document.getElementById(id))
      .filter(Boolean);
    if (!observedSections.length) return undefined;

    let frame = null;
    const updateActiveSection = () => {
      frame = null;
      // Percentage observer margins are width-relative and can collapse the
      // reading window on wide screens. Use the viewport's actual reading line.
      const readingLine = Math.min(window.innerHeight * 0.22, 180);
      const current = observedSections.reduce((active, section) => (
        section.getBoundingClientRect().top <= readingLine ? section : active
      ), observedSections[0]);
      setActiveId(current.id);
    };
    const scheduleUpdate = () => {
      if (frame === null) frame = window.requestAnimationFrame(updateActiveSection);
    };

    scheduleUpdate();
    window.addEventListener("scroll", scheduleUpdate, { passive: true });
    window.addEventListener("resize", scheduleUpdate);
    return () => {
      window.removeEventListener("scroll", scheduleUpdate);
      window.removeEventListener("resize", scheduleUpdate);
      if (frame !== null) window.cancelAnimationFrame(frame);
    };
  }, [sections]);

  return <>
    <nav className="article-toc article-toc-desktop" aria-label="Article contents" data-testid="article-desktop-table-of-contents">
      <p data-testid="article-desktop-contents-label">Contents</p>
      <ContentsLinks sections={sections} activeId={activeId} prefix="desktop" onNavigate={handleNavigate} />
    </nav>
    <div className="article-toc-mobile" data-testid="article-mobile-table-of-contents" onKeyDown={(event) => { if (event.key === "Escape") { setMobileOpen(false); event.currentTarget.querySelector("button")?.focus(); } }}>
      <button
        type="button"
        aria-expanded={mobileOpen}
        aria-controls="mobile-article-contents"
        onClick={() => setMobileOpen((open) => !open)}
        data-testid="mobile-table-of-contents-toggle"
      >
        <span>Contents</span>
        <span className="mobile-toc-current" data-testid="mobile-table-of-contents-current-section">
          {sections.find(({ id }) => id === activeId)?.title}
        </span>
        <ChevronDown aria-hidden="true" size={16} className={mobileOpen ? "is-open" : ""} />
      </button>
      <nav id="mobile-article-contents" className={mobileOpen ? "is-open" : ""} aria-label="Mobile article contents" aria-hidden={!mobileOpen} inert={!mobileOpen ? true : undefined} data-testid="mobile-table-of-contents-links">
        <ContentsLinks sections={sections} activeId={activeId} prefix="mobile" onNavigate={handleNavigate} />
      </nav>
    </div>
  </>;
};